/**
 * COMO VA MES A MES (VIS-T3, mision VISUAL-PERIODOS-V1-001; boceto #40563, va de Ares #40724).
 *
 * La ficha de persona dice -como va este mes-; esto dice si ESA persona mejora: una fila por mes, los ultimos 6. Nunca
 * compara con otra persona. Audiencia: el dueno o el administrador (la version del propio colaborador vive en el Arcade).
 *
 * De donde sale cada columna, una sola definicion por cosa:
 *   asistencia  el detector de anomalias (GET asistencia/anomalias por mes): se agrupa lo que el detector ya clasifico,
 *               el panel no reclasifica nada;
 *   danos       la funcion unica de danos de A, por la persona (D-41: su parte), via GET personas/{id}/mes-a-mes;
 *   arqueos     v_arqueos_unificados por colaborador, por el mismo GET. Mientras A no lo sirva: -falta publicar-.
 * Cada columna sale solo si esa persona tiene ese dato en algun mes (regla de #3777: sin columnas vacias).
 *
 * Sin DOM: lo prueba scripts/check-mes-a-mes.mjs.
 *
 * @module application/personas/mesAMes
 */

export const MESES_MES_A_MES = 6;

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const NOMBRES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** 'yyyy-MM' -> 'noviembre 2026'. */
export const nombreDelMes = (mes) => {
  const m = /^(\d{4})-(\d{2})$/.exec(texto(mes));
  return m ? `${NOMBRES[Number(m[2]) - 1]} ${m[1]}` : texto(mes);
};

/**
 * Los ultimos n meses contando el de hoy, del mas reciente al mas viejo: [{mes, desde, hasta}]. El mes de hoy va hasta hoy
 * (no hasta fin de mes: el detector marcaria como faltas los dias que no han llegado). hoy = 'yyyy-MM-dd' de Bogota.
 */
export function mesesHacia(hoy, n = MESES_MES_A_MES) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto(hoy));
  if (!m) return [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const total = Number(m[1]) * 12 + (Number(m[2]) - 1) - i;
    const anio = Math.floor(total / 12);
    const mes = (total % 12) + 1;
    const mm = String(mes).padStart(2, '0');
    const ultimo = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
    out.push({ mes: `${anio}-${mm}`, desde: `${anio}-${mm}-01`, hasta: i === 0 ? hoy : `${anio}-${mm}-${String(ultimo).padStart(2, '0')}` });
  }
  return out;
}

/**
 * La asistencia de un mes con las filas del detector de ESA persona en ese mes: dias del detector, dias OK, tardanzas y
 * sus minutos (desvioMin de las TARDANZA) y el resto de novedades. El dia en curso no cuenta. null si no hay filas.
 */
export function asistenciaDelMes(filas) {
  const lista = (Array.isArray(filas) ? filas : []).filter((f) => texto(f.clasificacion ?? f.Clasificacion).toUpperCase() !== 'DIA_EN_CURSO');
  if (!lista.length) return null;
  let ok = 0, tardanzas = 0, minutosTarde = 0, novedades = 0;
  for (const f of lista) {
    const c = texto(f.clasificacion ?? f.Clasificacion).toUpperCase();
    if (c === 'OK') ok++;
    else if (c === 'TARDANZA') { tardanzas++; minutosTarde += Math.max(0, num(f.desvioMin ?? f.DesvioMin) ?? 0); }
    else novedades++;
  }
  return { dias: lista.length, ok, tardanzas, minutosTarde, novedades };
}

/**
 * Lo que dice GET personas/{id}/mes-a-mes (contrato propuesto a A en #40735), en camel o Pascal.
 * -> {publicado, porMes: {'yyyy-MM|businessId': {danos, arqueos}}}; publicado=false si la API no lo sirve todavia.
 */
export function leerMesAMes(res) {
  if (!res || !res.ok) return { publicado: false, motivo: res?.code === 404 ? 'NO_PUBLICADO' : res?.code === 403 ? 'PERMISO' : 'RED', porMes: {} };
  const d = res.data?.data ?? res.data?.Data ?? res.data ?? {};
  const meses = Array.isArray(d.meses ?? d.Meses) ? (d.meses ?? d.Meses) : [];
  const porMes = {};
  for (const m of meses) {
    const dn = m.danos ?? m.Danos;
    const ar = m.arqueos ?? m.Arqueos;
    porMes[`${texto(m.mes ?? m.Mes)}|${texto(m.businessId ?? m.BusinessId)}`] = {
      danos: dn ? { pesos: num(dn.pesos ?? dn.Pesos) ?? 0, cantidad: num(dn.cantidad ?? dn.Cantidad) ?? 0 } : null,
      arqueos: ar ? { cuantos: num(ar.cuantos ?? ar.Cuantos) ?? 0, diferenciaNeta: num(ar.diferenciaNeta ?? ar.DiferenciaNeta) ?? 0 } : null,
    };
  }
  return { publicado: true, motivo: null, porMes };
}

/**
 * Las filas de un negocio, del mes mas reciente al mas viejo, y las columnas que tienen dato.
 * meses = mesesHacia(...); asistencias = [asistenciaDelMes por mes, mismo orden]; api = leerMesAMes(...).
 * -> {columnas: ['asistencia'|'danos'|'arqueos'], filas: [{mes, nombre, asistencia, danos, arqueos}]}
 */
export function tablaDelNegocio(meses, asistencias, api, businessId) {
  const filas = (meses || []).map((m, i) => {
    const deApi = api?.porMes?.[`${m.mes}|${texto(businessId)}`] || {};
    return { mes: m.mes, nombre: nombreDelMes(m.mes), asistencia: asistencias?.[i] ?? null, danos: deApi.danos ?? null, arqueos: deApi.arqueos ?? null };
  });
  const columnas = ['asistencia', 'danos', 'arqueos'].filter((c) => filas.some((f) => f[c] !== null));
  return { columnas, filas };
}

/** Lo que se lee en cada celda; una celda sin dato dice -sin dato-, nunca 0. */
export function textoDeCelda(columna, valor, pesos = (n) => '$' + Math.round(n).toLocaleString('es-CO')) {
  if (valor === null || valor === undefined) return 'sin dato';
  if (columna === 'asistencia') {
    const partes = [`${valor.ok} de ${valor.dias} día${valor.dias === 1 ? '' : 's'} bien`];
    if (valor.tardanzas) partes.push(`${valor.tardanzas} tarde${valor.tardanzas === 1 ? '' : 's'} (${valor.minutosTarde} min)`);
    if (valor.novedades) partes.push(`${valor.novedades} con novedad`);
    return partes.join(' · ');
  }
  if (columna === 'danos') return valor.cantidad ? `${pesos(valor.pesos)} en ${valor.cantidad} daño${valor.cantidad === 1 ? '' : 's'}` : 'sin daños';
  if (columna === 'arqueos') {
    if (!valor.cuantos) return 'no hizo arqueos';
    const dif = valor.diferenciaNeta;
    return `${valor.cuantos} arqueo${valor.cuantos === 1 ? '' : 's'} · ${dif === 0 ? 'cuadraron' : (dif > 0 ? 'sobró ' : 'faltó ') + pesos(Math.abs(dif))}`;
  }
  return '';
}

export const TITULOS = { asistencia: 'Asistencia', danos: 'Daños (su parte)', arqueos: 'Arqueos' };

/** Por que faltan daños y arqueos por mes, en palabras del dueño. */
export function avisoDeLaApi(api) {
  if (api?.publicado) return '';
  if (api?.motivo === 'PERMISO') return 'Tu usuario no puede ver los daños y arqueos de esta persona.';
  if (api?.motivo === 'NO_PUBLICADO') return 'Los daños y arqueos por mes se encienden cuando la API los publique; por ahora va la asistencia.';
  return 'No pudimos traer los daños y arqueos por mes ahora; la asistencia sí.';
}
